import { ObservableMap, makeAutoObservable } from 'mobx';

import { analytics, dataLayerActions } from '@/app/parts/Analytics';
import { getMaxBet } from '@/modules/Betslip/parts/MaxBet/parts/getMaxBet';

import { place } from '@/lib/api/auth';
import { sendMsg } from '@/lib/api/postMessageAPI';
import { getSwipeBets } from '@/lib/api/recommendations';
import { Socket } from '@/lib/api/Socket';
import { betStatus } from '@/lib/statuses';
import { getFromWebSocket } from '@/helpers/getFromWebSocket';
import { LS } from '@/helpers/localStorageHelper';
import { timeout } from '@/helpers/timeout';
import { betTrackMark } from '@/config/constants';

import { mobx } from '..';
import type { TRootStore } from '../RootStoreMobx';

const MAX_PROCESS_REQUESTS = 5;
const BET_TYPE = 'swipe_bet';

class SwipeBets {
  constructor(root: TRootStore) {
    makeAutoObservable(this);
    this.root = root;

    // Initialize from localStorage once during construction
    const storedPreset = LS.get('swipeBetsPreset');
    if (storedPreset) {
      this.swipeBetsPreset = storedPreset;
    }

    const storedTutorialVisited = !!LS.get('swipeBetsTutorialVisited');
    this.swipeBetsTutorialVisited = storedTutorialVisited;

    // Initialize excludeIds from localStorage
    const storedExcludedIds = LS.get('swipeBetsExcludedIds');
    if (storedExcludedIds) {
      try {
        const parsed = JSON.parse(storedExcludedIds as unknown as string);
        if (Array.isArray(parsed)) {
          this.excludeIds = parsed.filter((id) => typeof id === 'string');
        }
      } catch {
        // ignore corrupted data
      }
    }
  }

  root: TRootStore;

  swipeBets: ISwipeBetItem[] = [];

  swipeBetsPreset: string | null = null;

  swipeBetsTutorialVisited: boolean = false;

  // swipeBetsCounter: number = 12;

  swipeBetsData: ISwipeBetsData | null = null;

  visibleCards: ISwipeBetItem[] = [];

  currentCardIndex: number = 0;

  isLoadingMoreCards: boolean = false;

  isLoadingInitialCards: boolean = false;

  isCardReturned: boolean = false;

  cardsPageNumber: number = 1;

  swipeBetsMaxBet: number | null = null;

  lastRemovedCard: IEnrichedSwipeBetCard | null = null;

  swipeBetCurrentTab: 'custom' | 'max' = 'custom';

  excludeIds: string[] = [];

  isAnimationInProgress = false;

  lastRequestParams: { disciplinesIds: string[]; pageSize: number } = {
    disciplinesIds: [],
    pageSize: 100,
  };

  subscribedMarketIds: string[] = [];
  subscribedEventIds: string[] = [];
  subscribedTournamentIds: string[] = [];

  swipeBetsMarkets: { resolved: object[]; rejected: string[] } = { rejected: [], resolved: [] };

  swipeBetsEvents: { resolved: object[]; rejected: string[] } = { rejected: [], resolved: [] };

  swipeBetsTournaments: { resolved: object[]; rejected: string[] } = { rejected: [], resolved: [] };

  // SwipeBet processing state
  processRequestCounter: number = 0;
  isWaitingSwipeBet: boolean = false;
  swipeBetData: any = null;
  hasSwipeBetError: boolean = false;

  // Helper flag to avoid spamming requests when enriched cards are empty
  hasTriedLoadingNextPageForEmptyCards: boolean = false;

  private persistExcludedIds() {
    try {
      LS.set('swipeBetsExcludedIds', JSON.stringify(this.excludeIds));
    } catch {
      // ignore localStorage errors
    }
  }

  addExcludedId(id: string) {
    if (!id) return;
    if (!this.excludeIds.includes(id)) {
      this.excludeIds = [...this.excludeIds, id];
      this.persistExcludedIds();
    }
  }

  async initSocketConnection() {
    try {
      await Socket.initSocket();
    } catch (error) {
      console.error('SwipeBets: Failed to initialize socket connection', error);
    }
  }

  setSwipeBetsPreset(value: string) {
    this.swipeBetsPreset = value;
  }

  getSwipeBetsPreset() {
    return this.swipeBetsPreset;
  }

  getSwipeBetsTutorialVisited() {
    return this.swipeBetsTutorialVisited;
  }

  setSwipeBetsTutorialVisited(value: boolean) {
    LS.set('swipeBetsTutorialVisited', value);
    this.swipeBetsTutorialVisited = value;
  }

  // updateSwipeBetsCounter() {
  //   this.swipeBetsCounter++;
  // }

  async updateVisibleCards() {
    if (!this.isCardReturned) {
      this.currentCardIndex++;
    }

    const visibleCards =
      this.swipeBetsData?.data.slice(this.currentCardIndex, this.currentCardIndex + 3) || [];

    // Build new IDs with proper format
    const allTournamentIds = visibleCards.map((item) => {
      return `1.${item.disciplineId}.${item.tournamentId}`;
    });
    const allEventIds = visibleCards.map((item) => {
      return `1.${item.eventId}`;
    });
    const allMarketIds = visibleCards.map((item, index) => {
      return `${allEventIds[index]}.${item.eventMarketId}`;
    });

    // Deduplicate IDs (tournaments can be shared between multiple events)
    const newTournamentIds = [...new Set(allTournamentIds)];
    const newEventIds = [...new Set(allEventIds)];
    const newMarketIds = [...new Set(allMarketIds)];

    // Find IDs to unsubscribe (old IDs that are not in new IDs)
    const tournamentsToUnsubscribe = this.subscribedTournamentIds.filter(
      (id) => !newTournamentIds.includes(id),
    );
    const eventsToUnsubscribe = this.subscribedEventIds.filter((id) => !newEventIds.includes(id));
    const marketsToUnsubscribe = this.subscribedMarketIds.filter(
      (id) => !newMarketIds.includes(id),
    );

    // Find IDs to subscribe (new IDs that are not in old IDs)
    const tournamentsToSubscribe = newTournamentIds.filter(
      (id) => !this.subscribedTournamentIds.includes(id),
    );
    const eventsToSubscribe = newEventIds.filter((id) => !this.subscribedEventIds.includes(id));
    const marketsToSubscribe = newMarketIds.filter((id) => !this.subscribedMarketIds.includes(id));

    // Unsubscribe from entities that are no longer visible
    if (tournamentsToUnsubscribe.length > 0) {
      Socket.unsubscribe('tournament', tournamentsToUnsubscribe);
    }
    if (eventsToUnsubscribe.length > 0) {
      Socket.unsubscribe('event', eventsToUnsubscribe);
    }
    if (marketsToUnsubscribe.length > 0) {
      this.unsubscribeFromMarkets(marketsToUnsubscribe);
    }

    // Subscribe to new entities (only if they're not already subscribed)
    if (tournamentsToSubscribe.length > 0) {
      Socket.subscribe('tournament', tournamentsToSubscribe);
    }
    if (eventsToSubscribe.length > 0) {
      Socket.subscribe('event', eventsToSubscribe);
    }
    if (marketsToSubscribe.length > 0) {
      this.subscribeToMarkets(marketsToSubscribe);
    }

    // Update stored subscriptions
    this.subscribedTournamentIds = newTournamentIds;
    this.subscribedEventIds = newEventIds;
    this.subscribedMarketIds = newMarketIds;

    // Only proceed if we have visible cards
    if (visibleCards.length === 0) {
      // If no visible cards, clear everything
      this.visibleCards = [];
      this.swipeBetsTournaments = { rejected: [], resolved: [] };
      this.swipeBetsEvents = { rejected: [], resolved: [] };
      this.swipeBetsMarkets = { rejected: [], resolved: [] };
      return;
    }

    // Load new entities data from WebSocket (only new entities)
    if (
      tournamentsToSubscribe.length > 0 ||
      eventsToSubscribe.length > 0 ||
      marketsToSubscribe.length > 0
    ) {
      const [newTournaments, newEvents, newMarkets] = await Promise.all([
        tournamentsToSubscribe.length > 0
          ? getFromWebSocket({
              entityName: 'tournament',
              ids: tournamentsToSubscribe,
            })
          : Promise.resolve({ rejected: [], resolved: [] }),
        eventsToSubscribe.length > 0
          ? getFromWebSocket({
              entityName: 'event',
              ids: eventsToSubscribe,
            })
          : Promise.resolve({ rejected: [], resolved: [] }),
        marketsToSubscribe.length > 0
          ? getFromWebSocket({
              entityName: 'market',
              ids: marketsToSubscribe,
            })
          : Promise.resolve({ rejected: [], resolved: [] }),
      ]);

      // Prepare updated data
      const updatedTournaments = {
        rejected: [...this.swipeBetsTournaments.rejected, ...newTournaments.rejected],
        resolved: [
          ...this.swipeBetsTournaments.resolved.filter((t: any) =>
            newTournamentIds.includes(t.lid),
          ),
          ...newTournaments.resolved,
        ],
      };

      const updatedEvents = {
        rejected: [...this.swipeBetsEvents.rejected, ...newEvents.rejected],
        resolved: [
          ...this.swipeBetsEvents.resolved.filter((e: any) => newEventIds.includes(e.lid)),
          ...newEvents.resolved,
        ],
      };

      const updatedMarkets = {
        rejected: [...this.swipeBetsMarkets.rejected, ...newMarkets.rejected],
        resolved: [
          ...this.swipeBetsMarkets.resolved.filter((m: any) => newMarketIds.includes(m.lid)),
          ...newMarkets.resolved,
        ],
      };

      this.visibleCards = visibleCards;
      this.swipeBetsTournaments = updatedTournaments;
      this.swipeBetsEvents = updatedEvents;
      this.swipeBetsMarkets = updatedMarkets;
    } else {
      this.visibleCards = visibleCards;
    }

    const remainingCards = (this.swipeBetsData?.data.length || 0) - this.currentCardIndex;
    if (remainingCards <= 3 && remainingCards > 0 && !this.isLoadingMoreCards) {
      this.cardsPageNumber++;
      await this.loadMoreSwipeBets({
        disciplinesIds: [],
        pageNumber: this.cardsPageNumber,
        pageSize: 100,
      });
    }
  }

  get enrichedCards(): IEnrichedSwipeBetCard[] {
    const enrichedCards = this.visibleCards
      .map((card) => {
        const expectedTournamentLid = `1.${card.disciplineId}.${card.tournamentId}`;
        const expectedEventLid = `1.${card.eventId}`;
        const expectedMarketLid = `${expectedEventLid}.${card.eventMarketId}`;

        const tournament = this.swipeBetsTournaments.resolved.find(
          (t: any) => t.lid === expectedTournamentLid,
        ) as any;

        const event = this.swipeBetsEvents.resolved.find(
          (e: any) => e.lid === expectedEventLid,
        ) as any;

        const market = this.swipeBetsMarkets.resolved.find(
          (m: any) => m.lid === expectedMarketLid,
        ) as any;

        const isEventInTournament = event?.pid === expectedTournamentLid;
        const isMarketInEvent = market?.lid?.startsWith(expectedEventLid);

        if (tournament && event && market && isEventInTournament && isMarketInEvent) {
          return {
            ...card,
            event,
            market,
            tournament,
          } as IEnrichedSwipeBetCard;
        }

        // Return null if relationships don't match
        // console.warn('SwipeBets: Card data mismatch', {
        //   card,
        //   hasEvent: !!event,
        //   hasMarket: !!market,
        //   hasTournament: !!tournament,
        //   isEventInTournament,
        //   isMarketInEvent,
        // });

        return null;
      })
      .filter((card): card is IEnrichedSwipeBetCard => card !== null);

    return enrichedCards;
  }

  subscribeToMarkets(marketIds: string[]) {
    if (marketIds.length > 0) {
      Socket.subscribe('market', marketIds);
    }
  }

  unsubscribeFromMarkets(marketIds: string[]) {
    if (marketIds.length > 0) {
      Socket.unsubscribe('market', marketIds);
    }
  }

  async getSwipeBetsData({
    disciplinesIds = [],
    excludeIds,
    pageSize = 100,
  }: {
    disciplinesIds?: string[];
    excludeIds?: string[];
    pageSize?: number;
  } = {}): Promise<void> {
    if (this.isLoadingInitialCards) {
      return;
    }

    try {
      this.isLoadingInitialCards = true;

      await this.initSocketConnection();

      // Save request params for future use
      this.lastRequestParams = { disciplinesIds, pageSize };

      const effectiveExcludeIds = excludeIds ?? this.excludeIds;

      const response = await getSwipeBets({
        disciplinesIds,
        excludeIds: effectiveExcludeIds,
        pageNumber: this.cardsPageNumber,
        pageSize,
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch swipe bets data');
      }

      this.swipeBetsData = response.data;

      // Check if data is empty but more pages exist - retry with next page
      const maxPages = Math.ceil(response.data.meta.totalRows / pageSize);
      if (
        response.data.data.length === 0 &&
        response.data.meta?.totalRows > 0 &&
        this.cardsPageNumber < maxPages
      ) {
        this.cardsPageNumber++;

        // Temporarily release the lock for recursive call
        this.isLoadingInitialCards = false;

        // Recursively call to fetch the next page
        return await this.getSwipeBetsData({
          disciplinesIds,
          excludeIds: effectiveExcludeIds,
          pageSize,
        });
      }

      const visibleCards =
        this.swipeBetsData?.data.slice(this.currentCardIndex, this.currentCardIndex + 3) || [];

      const allTournamentIds = visibleCards.map((item) => {
        return `1.${item.disciplineId}.${item.tournamentId}`;
      });
      const allEventIds = visibleCards.map((item) => {
        return `1.${item.eventId}`;
      });
      const allMarketIds = visibleCards.map((item, index) => {
        return `${allEventIds[index]}.${item.eventMarketId}`;
      });

      // Deduplicate IDs (tournaments can be shared between multiple events)
      const tournamentIds = [...new Set(allTournamentIds)];
      const eventIds = [...new Set(allEventIds)];
      const cardsMarketIds = [...new Set(allMarketIds)];

      // Unsubscribe from all previously subscribed entities
      if (this.subscribedMarketIds.length > 0) {
        Socket.unsubscribe('market', this.subscribedMarketIds);
      }
      if (this.subscribedEventIds.length > 0) {
        Socket.unsubscribe('event', this.subscribedEventIds);
      }
      if (this.subscribedTournamentIds.length > 0) {
        Socket.unsubscribe('tournament', this.subscribedTournamentIds);
      }

      // Subscribe to all entities for visible cards - fetch in parallel
      const [tournamentsData, eventsData, marketsData] = await Promise.all([
        getFromWebSocket({
          entityName: 'tournament',
          ids: tournamentIds,
        }),
        getFromWebSocket({
          entityName: 'event',
          ids: eventIds,
        }),
        getFromWebSocket({
          entityName: 'market',
          ids: cardsMarketIds,
        }),
      ]);

      // Set visibleCards FIRST, THEN set data (to ensure enrichedCards has cards to match)
      this.visibleCards = visibleCards;

      // Now set all data at once
      this.swipeBetsTournaments = tournamentsData;
      this.swipeBetsEvents = eventsData;
      this.swipeBetsMarkets = marketsData;

      // Store current subscriptions
      this.subscribedMarketIds = cardsMarketIds;
      this.subscribedEventIds = eventIds;
      this.subscribedTournamentIds = tournamentIds;

      // Subscribe via socket to all entities for visible cards
      Socket.subscribe('tournament', tournamentIds);
      Socket.subscribe('event', eventIds);
      Socket.subscribe('market', cardsMarketIds);
    } catch (error) {
      console.error('Error fetching swipe bets data', error);
    } finally {
      this.isLoadingInitialCards = false;
    }
  }

  /**
   * When we have raw visible cards but can't enrich them (no tournament/event/market data),
   * try to load the next page of recommendations and rebuild visible/enriched cards from it.
   */
  async loadNextPageWhenEnrichedEmpty() {
    if (
      this.hasTriedLoadingNextPageForEmptyCards ||
      this.isLoadingMoreCards ||
      !this.swipeBetsData
    ) {
      return;
    }

    const { disciplinesIds, pageSize } = this.lastRequestParams;
    const { totalRows } = this.swipeBetsData.meta;

    const maxPages = Math.ceil(totalRows / pageSize);
    if (this.cardsPageNumber >= maxPages) {
      return;
    }

    try {
      this.hasTriedLoadingNextPageForEmptyCards = true;

      this.cardsPageNumber++;
      await this.loadMoreSwipeBets({
        disciplinesIds,
        pageNumber: this.cardsPageNumber,
        pageSize,
      });

      // Reset index to start from the first card of the newly loaded page
      this.currentCardIndex = 0;
      await this.updateVisibleCards();
    } finally {
      this.hasTriedLoadingNextPageForEmptyCards = false;
    }
  }

  async loadMoreSwipeBets({
    disciplinesIds = [],
    excludeIds,
    pageNumber = 1,
    pageSize = 100,
  }: {
    disciplinesIds?: string[];
    excludeIds?: string[];
    pageNumber?: number;
    pageSize?: number;
  } = {}) {
    if (this.isLoadingMoreCards || !this.swipeBetsData) {
      return;
    }

    try {
      this.isLoadingMoreCards = true;

      const effectiveExcludeIds = excludeIds ?? this.excludeIds;

      const response = await getSwipeBets({
        disciplinesIds,
        excludeIds: effectiveExcludeIds,
        pageNumber,
        pageSize,
      });

      if (response.status === 200 && response.data.data.length > 0) {
        // Append new data to existing data
        this.swipeBetsData = {
          data: response.data.data,
          meta: response.data.meta,
        };
        // console.log('SwipeBets: More cards loaded successfully');
      }
    } catch (error) {
      console.error('Error loading more swipe bets data', error);
    } finally {
      this.isLoadingMoreCards = false;
    }
  }

  getSwipeBetPossibleWin = (amount: number, odd: number) => {
    const { roundCurrency } = this.root.profile;

    if (this.swipeBetCurrentTab === 'max' && this.swipeBetsMaxBet) {
      return roundCurrency(this.swipeBetsMaxBet * odd) || 0;
    } else {
      return roundCurrency(amount * odd) || 0;
    }
  };

  returnLastRemovedCard = async () => {
    if (!this.lastRemovedCard) {
      return;
    }

    const cardToReturn = this.lastRemovedCard;
    this.isCardReturned = true;

    if (this.currentCardIndex > 0) {
      this.currentCardIndex--;
    }

    const tournamentId = `1.${cardToReturn.disciplineId}.${cardToReturn.tournamentId}`;
    const eventId = `1.${cardToReturn.eventId}`;
    const marketId = `${eventId}.${cardToReturn.eventMarketId}`;

    const [tournamentData, eventData, marketData] = await Promise.all([
      getFromWebSocket({
        entityName: 'tournament',
        ids: [tournamentId],
      }),
      getFromWebSocket({
        entityName: 'event',
        ids: [eventId],
      }),
      getFromWebSocket({
        entityName: 'market',
        ids: [marketId],
      }),
    ]);

    Socket.subscribe('tournament', [tournamentId]);
    Socket.subscribe('event', [eventId]);
    Socket.subscribe('market', [marketId]);

    this.swipeBetsTournaments = {
      rejected: [...this.swipeBetsTournaments.rejected, ...tournamentData.rejected],
      resolved: [
        ...tournamentData.resolved,
        ...this.swipeBetsTournaments.resolved.filter((t: any) => t.lid !== tournamentId),
      ],
    };

    this.swipeBetsEvents = {
      rejected: [...this.swipeBetsEvents.rejected, ...eventData.rejected],
      resolved: [
        ...eventData.resolved,
        ...this.swipeBetsEvents.resolved.filter((e: any) => e.lid !== eventId),
      ],
    };

    this.swipeBetsMarkets = {
      rejected: [...this.swipeBetsMarkets.rejected, ...marketData.rejected],
      resolved: [
        ...marketData.resolved,
        ...this.swipeBetsMarkets.resolved.filter((m: any) => m.lid !== marketId),
      ],
    };

    if (!this.subscribedTournamentIds.includes(tournamentId)) {
      this.subscribedTournamentIds.push(tournamentId);
    }
    if (!this.subscribedEventIds.includes(eventId)) {
      this.subscribedEventIds.push(eventId);
    }
    if (!this.subscribedMarketIds.includes(marketId)) {
      this.subscribedMarketIds.push(marketId);
    }

    // Update visibleCards AFTER all data is loaded
    const newVisibleCards = [cardToReturn, ...this.visibleCards];
    if (newVisibleCards.length > 3) {
      newVisibleCards.pop();
    }
    this.visibleCards = newVisibleCards;

    this.lastRemovedCard = null;
  };

  makeSwipeBet = async ({
    bubblePostMessage = true,
    outcomeId,
    marketId,
    trackMarks = [betTrackMark.swipeBet],
  }: {
    bubblePostMessage?: boolean;
    outcomeId: string;
    marketId: string;
    trackMarks?: string[];
  }) => {
    const bet = mobx.betslip.initNewBet({
      id: outcomeId,
      marketId,
      trackMarks,
    });
    if (!bet) return;

    const {
      auth: { subPartnerId },
    } = this.root;

    if (bubblePostMessage) {
      sendMsg('clickPlaceBet', {
        betType: 'ordinar',
      });
    }

    const {
      profile: {
        user: { visitorId },
      },
    } = this.root;

    const placeBet = async () => {
      const preparedBet = this.prepareSwipeBet(bet);
      const currentAmount =
        Boolean(preparedBet) && 'amount' in preparedBet && typeof preparedBet.amount === 'number'
          ? preparedBet.amount
          : typeof preparedBet?.amount === 'string'
            ? parseFloat(`${preparedBet?.amount}`) || 0
            : null;

      if (!mobx.betslip.isValidBetAmount({ amount: currentAmount })) {
        return {
          response: {
            errorCode: 'invalidAmount',
            status: 'error',
          },
        };
      }
      const preparedBets = [preparedBet];

      try {
        const { data } = await place({
          accept_changes: true,
          bets: preparedBets,
          sub_partner_id: subPartnerId,
          visitorId,
        });

        if (data) {
          sendMsg('placeBet', {
            betType: 'ordinar',
            bets: preparedBets,
          });

          analytics.pushBetInfo({
            action: dataLayerActions.ADD_BET,
            bets: [bet],
            currentBetType: 'swipe_bet',
          });

          return { response: data.data[0] };
        }
      } catch (e) {
        sendMsg('placeBetError', {
          betType: 'ordinar',
          error: e.message,
        });
        console.error(`Can't place swipe bet: ${e.message}`);

        return {
          response: {
            placeBetError: e.message,
          },
        };
      }
    };

    if (mobx.betslip.valid([bet])) {
      const { response } = await placeBet();
      return { bet, response };
    } else {
      return { response: mobx.betslip.betStatuses[0] };
    }
  };

  prepareSwipeBet = (bet) => {
    const { id, pidLid, trackMarks } = bet;
    const {
      profile: {
        currency: { name },
      },
      store: { getOutcomeByLID },
    } = this.root;

    const outcomeItem = getOutcomeByLID(id, pidLid);
    if (outcomeItem) {
      const amount =
        this.swipeBetCurrentTab === 'max' && this.swipeBetsMaxBet
          ? this.swipeBetsMaxBet
          : (this.swipeBetsPreset ?? 0);

      const result = {
        amount: parseFloat(`${amount}`) || 0,
        coefficient: parseFloat(outcomeItem.price.toString()),
        currency: name,
        outcomeId: id,
        trackMarks,
      };

      return result;
    } else {
      return null;
    }
  };

  setHasSwipeBetError = (hasError: boolean) => {
    this.hasSwipeBetError = hasError;
  };

  setIsWaitingSwipeBet = (value: boolean) => {
    this.isWaitingSwipeBet = value;
  };

  swipeBetProcessRequest = async (
    data: any,
    bet: any,
  ): Promise<{ delay: number; hasError: boolean; status: number }> => {
    const { status, delay = 0, id: betId, errorCode } = data;

    this.swipeBetData = data;

    // Якщо статус 0 (очікування) і не перевищено ліміт спроб
    if (+status === 0 && this.processRequestCounter < MAX_PROCESS_REQUESTS) {
      await timeout(delay);

      const {
        data: { data: newData },
      } = await mobx.betslip.processRequest(betId);

      // Якщо коефіцієнт змінився (статус 2)
      if (newData?.status === 2) {
        mobx.betslip.emitCefChangedAlert(newData.coefficient, newData.placebetCoefficient);
      }

      this.processRequestCounter += 1;

      // Рекурсивний виклик для перевірки нового статусу
      return await this.swipeBetProcessRequest(newData, bet);
    }

    // Завершення обробки
    this.isWaitingSwipeBet = false;
    this.processRequestCounter = 0;

    analytics.pushBetInfo({
      action: dataLayerActions.PLACE_BET,
      bets: [bet],
      currentBetType: BET_TYPE,
      transactionId: betId,
    });

    // Обробка помилок
    if (errorCode !== undefined) {
      this.hasSwipeBetError = true;

      analytics.pushBetInfo({
        action: dataLayerActions.REFUND,
        bets: [bet],
        currentBetType: BET_TYPE,
        errorCode,
        transactionId: betId,
      });

      // Відправка повідомлення партнеру якщо не вистачає коштів
      if (errorCode === 2) {
        sendMsg('placeBetFailure', {
          status: betStatus.iframeMessage.notEnoughMoney.text,
          statusId: betStatus.iframeMessage.notEnoughMoney.statusId,
        });
      }

      return { delay, hasError: true, status: +status };
    }

    // Успішно
    this.hasSwipeBetError = false;
    return { delay, hasError: false, status: +status };
  };

  getCurrentMaxBet = async () => {
    const item = this.enrichedCards[0];
    if (item) {
      const bet = mobx.betslip.initNewBet({
        id: item.outcomeId,
        marketId: item.market.lid,
        trackMarks: [betTrackMark.swipeBet],
      });
      const queryString = (() => {
        const params = new URLSearchParams();
        params.append('outcomeIds[]', bet.id);
        params.append('currency', mobx.profile.currency.name);
        return params.toString();
      })();

      const amount = await getMaxBet({
        queryString,
        setIsLoading: () => {},
      });

      this.swipeBetsMaxBet = amount;
    } else {
      this.swipeBetsMaxBet = null;
    }
  };

  handleSwipeBet = async (marketId: string, outcomeId: string) => {
    // Якщо вже обробляємо ставку, не дозволяємо нову
    if (this.swipeBetData) {
      return {
        delay: 0,
        hasError: true,
        status: -1,
      };
    }

    // Скидаємо попередню помилку
    this.hasSwipeBetError = false;
    this.isWaitingSwipeBet = true;

    // Створюємо та розміщуємо ставку
    const { bet, response } = await mobx.swipeBets.makeSwipeBet({
      marketId,
      outcomeId,
    });

    if (!bet || !response) {
      this.isWaitingSwipeBet = false;
      this.hasSwipeBetError = true;
      return {
        delay: 0,
        hasError: true,
        status: -1,
      };
    }

    // Аналітика початку оформлення ставки
    analytics.pushBetInfo({
      action: dataLayerActions.BEGIN_CHECKOUT,
      bets: [bet],
      currentBetType: BET_TYPE,
    });

    mobx.betslip.setQuickBetsResolveCounter(true);

    // Обробляємо відповідь
    const result = await this.swipeBetProcessRequest(response, bet);

    // Оновлюємо баланс
    mobx.profile.updateBalance();

    // Очищаємо дані після обробки
    this.swipeBetData = null;

    return result;
  };

  get isMarketInLine(): boolean {
    return this.root.store.markets.has(this.enrichedCards[0]?.market.lid);
  }

  cleanUpSwipeBets() {
    // Unsubscribe from all entities
    if (this.subscribedMarketIds.length > 0) {
      Socket.unsubscribe('market', this.subscribedMarketIds);
      this.subscribedMarketIds = [];
    }
    if (this.subscribedEventIds.length > 0) {
      Socket.unsubscribe('event', this.subscribedEventIds);
      this.subscribedEventIds = [];
    }
    if (this.subscribedTournamentIds.length > 0) {
      Socket.unsubscribe('tournament', this.subscribedTournamentIds);
      this.subscribedTournamentIds = [];
    }

    // Clear all data
    this.swipeBetsData = null;
    this.visibleCards = [];
    this.currentCardIndex = 0;
    this.isLoadingMoreCards = false;
    this.isLoadingInitialCards = false;
    this.cardsPageNumber = 1;
    this.swipeBetsTournaments = { rejected: [], resolved: [] };
    this.swipeBetsEvents = { rejected: [], resolved: [] };
  }
}

export { SwipeBets };

export interface ISwipeBetItem {
  disciplineId: string;
  eventDate: string;
  eventId: string;
  eventMarketId: string;
  eventMarketName: string;
  eventName: string;
  eventSlug: string;
  eventTopIndex: number;
  outcomeId: string;
  outcomeName: string;
  price: number;
  sortIndex: number;
  tier: number;
  tournamentId: string;
  _id: string;
}

interface ISwipeBetsData {
  data: ISwipeBetItem[];
  meta: {
    pageNumber: number;
    pageSize: number;
    totalRows: number;
  };
}

export interface IEnrichedSwipeBetCard extends ISwipeBetItem {
  tournament: any; // Tournament entity from WebSocket
  event: any; // Event entity from WebSocket
  market: any; // Market entity from WebSocket
}
